import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, UserPlus, X, Clock } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable modal for assigning an existing resource to MORE students.
 * No file duplication — appends student IDs to the resource's assigned_to list.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - resourceTitle: string (display)
 *  - classStudents: [{user_id|id, first_name, last_name, username}]
 *  - currentAssignedTo: string[]   // existing assigned_to (null/[] = "all students")
 *  - onAssign: async (selectedIds) => result   // caller performs the API call
 *  - onAssigned?: () => void
 */
export default function ResourceReuseModal({
  open, onClose, resourceTitle, classStudents = [], currentAssignedTo, onAssign, onAssigned,
  // iter75 · Pending scheduled batches management.
  contentId, token, scheduledAssignments = [],
}) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [editingBatch, setEditingBatch] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');

  const studentNameById = useMemo(() => {
    const map = {};
    classStudents.forEach(s => {
      const id = s.id || s.user_id;
      map[id] = `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.username || id;
    });
    return map;
  }, [classStudents]);

  const cancelBatch = async (batchId) => {
    try {
      await axios.delete(`${API}/content/${contentId}/scheduled-assignment/${batchId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Programación anulada');
      onAssigned?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo anular la programación');
    }
  };

  const saveBatchDate = async (batchId) => {
    if (!editDate || !editTime) {
      toast.error('Indica fecha y hora');
      return;
    }
    try {
      await axios.patch(`${API}/content/${contentId}/scheduled-assignment/${batchId}`,
        { scheduled_for: new Date(`${editDate}T${editTime}`).toISOString() },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Fecha actualizada');
      setEditingBatch(null);
      onAssigned?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar la fecha');
    }
  };

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelected(new Set());
      setScheduleEnabled(false);
      setScheduledDate('');
      setScheduledTime('');
    }
  }, [open]);

  const assignedAll = !currentAssignedTo || currentAssignedTo.length === 0;
  const assignedSet = useMemo(() => new Set(currentAssignedTo || []), [currentAssignedTo]);

  // Eligible students = those NOT already assigned (or all if resource was for everyone)
  const eligible = useMemo(() => {
    const list = classStudents.map(s => ({
      id: s.id || s.user_id,
      name: `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.username || (s.id || s.user_id),
    })).filter(s => !!s.id);
    if (assignedAll) return [];
    return list.filter(s => !assignedSet.has(s.id));
  }, [classStudents, assignedAll, assignedSet]);

  const filtered = useMemo(() => {
    if (!query.trim()) return eligible;
    const q = query.trim().toLowerCase();
    return eligible.filter(s => s.name.toLowerCase().includes(q));
  }, [eligible, query]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(s => s.id)));
  };

  const handleAssign = async () => {
    if (selected.size === 0) {
      toast.error('Selecciona al menos un estudiante');
      return;
    }
    setSubmitting(true);
    try {
      const scheduledFor = (scheduleEnabled && scheduledDate && scheduledTime)
        ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString()
        : null;
      const res = await onAssign(Array.from(selected), scheduledFor);
      if (res?.scheduled?.length) {
        toast.success(`Programado para ${res.scheduled.length} estudiante(s)`);
      } else {
        const added = res?.added?.length ?? selected.size;
        toast.success(`Asignado a ${added} estudiante(s)`);
      }
      if (onAssigned) onAssigned();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Error al asignar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto" data-testid="reuse-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Montserrat' }}>
            <UserPlus className="w-5 h-5 text-blue-500" />
            Asignar recurso a estudiantes
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          {resourceTitle && (
            <div className="text-xs text-slate-400">
              Recurso: <span className="font-medium text-slate-600">{resourceTitle}</span>
            </div>
          )}

          {scheduledAssignments.length > 0 && (
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 space-y-3" data-testid="pending-assignments-block">
              <p className="text-sm font-bold text-violet-800 flex items-center gap-1.5">
                <Clock className="w-4 h-4" /> Asignaciones programadas
              </p>
              {scheduledAssignments.map(batch => {
                const when = new Date(batch.scheduled_for);
                const names = (batch.student_ids || []).map(id => studentNameById[id] || id);
                return (
                  <div key={batch.id} className="bg-white rounded-lg border border-violet-100 p-3" data-testid={`pending-assignment-${batch.id}`}>
                    <p className="text-xs text-slate-700 font-semibold">
                      {when.toLocaleDateString()} · {when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                      {names.length} estudiante(s): {names.join(', ')}
                    </p>
                    {editingBatch === batch.id ? (
                      <div className="mt-2 space-y-2">
                        <div className="flex gap-2">
                          <Input type="date" value={editDate} onChange={e => setEditDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]} className="bg-white h-8 text-xs"
                            data-testid={`pending-edit-date-${batch.id}`} />
                          <Input type="time" value={editTime} onChange={e => setEditTime(e.target.value)}
                            className="bg-white h-8 text-xs" data-testid={`pending-edit-time-${batch.id}`} />
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" className="h-7 text-xs bg-violet-600 hover:bg-violet-700"
                            onClick={() => saveBatchDate(batch.id)} data-testid={`pending-save-${batch.id}`}>
                            Guardar fecha
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={() => setEditingBatch(null)} data-testid={`pending-cancel-edit-${batch.id}`}>
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => {
                            setEditingBatch(batch.id);
                            setEditDate(when.toISOString().split('T')[0]);
                            setEditTime(when.toTimeString().slice(0, 5));
                          }}
                          data-testid={`pending-reschedule-${batch.id}`}>
                          Cambiar fecha
                        </Button>
                        <Button size="sm" variant="outline"
                          className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => cancelBatch(batch.id)}
                          data-testid={`pending-annul-${batch.id}`}>
                          Anular
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {assignedAll ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
              Este recurso ya está asignado a <strong>todos los estudiantes</strong> de la clase.
              Los nuevos estudiantes que se unan también lo verán automáticamente.
            </div>
          ) : eligible.length === 0 ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700">
              Todos los estudiantes de la clase ya tienen este recurso.
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre..."
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  className="pl-9"
                  data-testid="reuse-search-input"
                />
                {query && (
                  <button
                    onClick={() => setQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="flex items-center justify-between text-xs text-slate-400 px-1">
                <span>{filtered.length} disponible{filtered.length === 1 ? '' : 's'}</span>
                {filtered.length > 0 && (
                  <button
                    onClick={toggleAll}
                    className="text-blue-600 hover:underline font-medium"
                    data-testid="reuse-toggle-all"
                  >
                    {selected.size === filtered.length ? 'Quitar selección' : 'Seleccionar todos'}
                  </button>
                )}
              </div>

              <div className="max-h-72 overflow-y-auto border border-slate-100 rounded-lg" data-testid="reuse-student-list">
                {filtered.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">Sin resultados</p>
                ) : filtered.map(s => (
                  <label
                    key={s.id}
                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0"
                    data-testid={`reuse-student-${s.id}`}
                  >
                    <Checkbox
                      checked={selected.has(s.id)}
                      onCheckedChange={() => toggle(s.id)}
                    />
                    <span className="text-sm text-slate-700 flex-1">{s.name}</span>
                  </label>
                ))}
              </div>
            </>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-blue-500" /> Programar publicacion
              </Label>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setScheduleEnabled(false)}
                  className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${!scheduleEnabled ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
                  data-testid="reuse-publish-now-btn">Publicar ahora</button>
                <button type="button" onClick={() => setScheduleEnabled(true)}
                  className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${scheduleEnabled ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
                  data-testid="reuse-schedule-later-btn">Programar para despues</button>
              </div>
            </div>
            {scheduleEnabled && (
              <div className="flex gap-3 mt-2">
                <div className="flex-1">
                  <Label className="text-xs text-slate-500 mb-1 block">Fecha</Label>
                  <Input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)}
                    className="bg-white" data-testid="reuse-schedule-date-input" min={new Date().toISOString().split('T')[0]} />
                </div>
                <div className="flex-1">
                  <Label className="text-xs text-slate-500 mb-1 block">Hora</Label>
                  <Input type="time" value={scheduledTime} onChange={e => setScheduledTime(e.target.value)}
                    className="bg-white" data-testid="reuse-schedule-time-input" />
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={onClose}
              data-testid="reuse-cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              disabled={submitting || selected.size === 0 || assignedAll || eligible.length === 0}
              onClick={handleAssign}
              data-testid="reuse-assign-btn"
            >
              {submitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>Asignar ({selected.size})</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
